import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { FileText, History, ChevronDown, ChevronUp, Eye } from "lucide-react";
import { toast } from "react-toastify";
import axios from "axios";
import Select from 'react-select';

const API_BASE_URL = `${process.env.REACT_APP_API_URL?.replace(/\/$/, "") || ""}/api`;

// Component for editing qualification
const EditQualificationDialog = ({ 
  isOpen, 
  onClose, 
  qualificationData, 
  fetchData, 
  userId,
  droppedFiles = [],
  isMultiMode = false 
}) => {
  const [loading, setLoading] = useState(false);
  const [qualificationDetails, setQualificationDetails] = useState({
    certificateNumber: "",
    expiryDate: "",
    certificate_file: null,
    qualificationTypeId: null,
  });
  const [qualificationTypes, setQualificationTypes] = useState([]);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [processedFiles, setProcessedFiles] = useState([]);
  const [skippedFiles, setSkippedFiles] = useState(new Set()); // Track skipped file indices
  const [companyQualifications, setCompanyQualifications] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [fileParseInfo, setFileParseInfo] = useState(null);
  const [parseWarnings, setParseWarnings] = useState({
    qualificationNotFound: false,
    userNotFound: false,
    dateInvalid: false
  });
  const [dataLoadedFromAPI, setDataLoadedFromAPI] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [versionHistory, setVersionHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const isEditMode = !!qualificationData;
  const hasMultipleFiles = droppedFiles.length > 1;
  const currentFile = droppedFiles[currentFileIndex];

  // Function to parse filename and extract information
  const parseFilename = (filename) => {
    try {
      // Remove file extension
      const nameWithoutExt = filename.replace(/\.[^/.]+$/, "");
      
      // Remove all spaces from the filename for easier parsing
      const cleanedName = nameWithoutExt.replace(/\s+/g, '');
      
      // Split by hyphen
      const parts = cleanedName.split('-');
      
      if (parts.length >= 3) {
        const username = parts[0];
        const trainingNumber = parts[1];
        const dateStr = parts[2];
        const qualificationNumber = parts.length >= 4 ? parts[3] : null;
        
        // Parse date (format: dd.mm.yy or dd.mm.yyyy)
        const dateParts = dateStr.split('.');
        if (dateParts.length === 3) {
          let [day, month, year] = dateParts.map(p => parseInt(p));
          
          // Handle 2-digit year (convert to 4-digit)
          if (year < 100) {
            year = year < 50 ? 2000 + year : 1900 + year;
          }
          
          const trainingDate = new Date(year, month - 1, day); // month is 0-indexed
          
          if (!isNaN(trainingDate.getTime())) {
            return {
              trainingNumber,
              username,
              trainingDate,
              qualificationNumber,
              originalFilename: filename
            };
          }
        }
      }
      
      return null;
    } catch (error) {
      console.error("Error parsing filename:", error);
      return null;
    }
  };

  // Function to calculate expiry date based on validity time
  // Function to format date for input field (avoiding timezone issues)
  const formatDateForInput = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const calculateExpiryDate = (trainingDate, validityDays) => {
    if (!trainingDate || !validityDays) return null;
    
    const expiryDate = new Date(trainingDate);
    
    // Handle special cases for years (365, 730, 1095, etc.)
    if (validityDays % 365 === 0) {
      const years = validityDays / 365;
      
      // Special handling for February 29th (leap year)
      if (expiryDate.getMonth() === 1 && expiryDate.getDate() === 29) {
        // Check if target year will be a leap year
        const targetYear = expiryDate.getFullYear() + years;
        const isTargetLeapYear = (targetYear % 4 === 0 && targetYear % 100 !== 0) || (targetYear % 400 === 0);
        
        if (!isTargetLeapYear) {
          // Target year is not a leap year, set to February 28th
          expiryDate.setFullYear(targetYear, 1, 28); // Month 1 = February (0-indexed)
        } else {
          // Target year is a leap year, February 29th is valid
          expiryDate.setFullYear(targetYear);
        }
      } else {
        // Normal date, just add years
        expiryDate.setFullYear(expiryDate.getFullYear() + years);
      }
    } else {
      // Add days normally for non-year periods
      expiryDate.setDate(expiryDate.getDate() + validityDays);
    }
    
    return expiryDate;
  };


  // Pobieranie danych dla trybu tworzenia
  useEffect(() => {
    if (isOpen && !isEditMode) {
      setDataLoadedFromAPI(false); // Reset flag for create mode
      const fetchData = async () => {
        try {
          const token = localStorage.getItem("access_token");
          
          // Fetch qualification types
          const qualTypesResponse = await axios.get(`${API_BASE_URL}/qualificationtype/`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setQualificationTypes(qualTypesResponse.data.map(type => ({
            value: type.id,
            label: type.name,
          })));

          // Fetch company qualifications
          const companyQualsResponse = await axios.get(`${API_BASE_URL}/company-qualifications/`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setCompanyQualifications(companyQualsResponse.data.results || companyQualsResponse.data);

          // Fetch users
          const usersResponse = await axios.get(`${API_BASE_URL}/users/`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setUsers(usersResponse.data.results || usersResponse.data);
          
        } catch (error) {
          console.error("Error fetching data:", error);
          toast.error("Failed to load required data");
        }
      };
      fetchData();
    }
  }, [isOpen, isEditMode]);

  // Parse current file and auto-populate data
  useEffect(() => {
    if (isOpen && !isEditMode && !dataLoadedFromAPI && currentFile && companyQualifications.length > 0 && users.length > 0) {
      console.log("Company qualifications:", companyQualifications);
      console.log("Users:", users);
      
      const parseInfo = parseFilename(currentFile.name);
      console.log("Parse info:", parseInfo);
      setFileParseInfo(parseInfo);
      
      // Reset warnings
      const warnings = {
        qualificationNotFound: false,
        userNotFound: false,
        dateInvalid: false
      };
      
      if (parseInfo) {
        // Find user by username
        const foundUser = users.find(u => u && u.username && u.username.toLowerCase() === parseInfo.username.toLowerCase());
        if (foundUser) {
          setSelectedUser(foundUser);
        } else {
          warnings.userNotFound = true;
          setSelectedUser(null);
        }
        
        // Find qualification type by training number
        const companyQual = companyQualifications.find(cq => cq && cq.training_number === parseInfo.trainingNumber);
        console.log("Found company qualification:", companyQual);
        
        if (companyQual && companyQual.qualification_type_data && companyQual.qualification_type_data.id) {
          const expiryDate = calculateExpiryDate(parseInfo.trainingDate, companyQual.validity_time);
          
          setQualificationDetails({
            certificateNumber: parseInfo.qualificationNumber || "",  // Certificate number from filename
            expiryDate: expiryDate ? formatDateForInput(expiryDate) : "",
            certificate_file: currentFile,
            qualificationTypeId: companyQual.qualification_type_data.id,
          });
          
          // Also update the qualification type display name for user reference
          console.log("Setting qualification type ID:", companyQual.qualification_type_data.id, "Name:", companyQual.qualification_type_data.name);
        } else {
          // If no company qualification found, just set the file
          warnings.qualificationNotFound = true;
          setQualificationDetails({
            certificateNumber: parseInfo.qualificationNumber || "",  // Certificate number from filename
            expiryDate: "",
            certificate_file: currentFile,
            qualificationTypeId: null,
          });
        }
        
        // Check if date is valid
        if (!parseInfo.trainingDate || isNaN(parseInfo.trainingDate.getTime())) {
          warnings.dateInvalid = true;
        }
      } else {
        // If parsing failed, just set the file
        setQualificationDetails({
          certificateNumber: "",
          expiryDate: "",
          certificate_file: currentFile,
          qualificationTypeId: null,
        });
      }
      
      setParseWarnings(warnings);
    }
  }, [isOpen, isEditMode, currentFile, companyQualifications, users]);

  // Ustawianie danych kwalifikacji w trybie edycji
  useEffect(() => {
    if (isOpen && isEditMode && qualificationData) {
      // Fetch fresh qualification data to ensure we have the latest information 
      const fetchFreshQualificationData = async () => {
        try {
          const token = localStorage.getItem("access_token");
          const response = await axios.get(
            `${API_BASE_URL}/userqualification/${qualificationData.id}/`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const freshData = response.data;
          
          setQualificationDetails({
            certificateNumber: freshData.certificate_number || "",
            expiryDate: freshData.expiry_date ? new Date(freshData.expiry_date).toISOString().split('T')[0] : "",
            certificate_file: null,
            qualificationTypeId: null,
          });
          setDataLoadedFromAPI(true);
        } catch (error) {
          console.error("Error fetching fresh qualification data:", error);
          // Fallback to original data if fetch fails
          setQualificationDetails({
            certificateNumber: qualificationData.certificate_number || "",
            expiryDate: qualificationData.expiry_date ? new Date(qualificationData.expiry_date).toISOString().split('T')[0] : "",
            certificate_file: null,
            qualificationTypeId: null,
          });
          setDataLoadedFromAPI(true);
        }
      };
      
      fetchFreshQualificationData();
    }
  }, [isOpen, qualificationData, isEditMode]);

  // Initialize processed files tracking
  useEffect(() => {
    if (isOpen && hasMultipleFiles) {
      setProcessedFiles(new Array(droppedFiles.length).fill(false));
      setCurrentFileIndex(0);
    }
  }, [isOpen, droppedFiles.length, hasMultipleFiles]);

  // Reset data loaded flag when modal closes
  useEffect(() => {
    if (!isOpen) {
      setDataLoadedFromAPI(false);
      setSkippedFiles(new Set());
      setShowHistory(false);
      setVersionHistory([]);
    }
  }, [isOpen]);

  // Fetch version history for edit mode
  const fetchVersionHistory = async () => {
    if (!qualificationData?.id) {
      setVersionHistory([]);
      return;
    }

    setLoadingHistory(true);
    try {
      const token = localStorage.getItem("access_token");
      const response = await axios.get(
        `${API_BASE_URL}/userqualification/${qualificationData.id}/?include_history=true`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.version_history) {
        setVersionHistory(response.data.version_history);
      } else {
        // If no version_history, this is the only version
        setVersionHistory([{
          id: response.data.id,
          version: response.data.version || 1,
          expiry_date: response.data.expiry_date,
          certificate_number: response.data.certificate_number,
          certificate_file: response.data.certificate_file,
          is_current: response.data.is_current,
          created_at: response.data.created_at,
          superseded_at: response.data.superseded_at
        }]);
      }
    } catch (error) {
      console.error("Error fetching version history:", error);
      setVersionHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const toggleHistory = () => {
    if (!showHistory) {
      fetchVersionHistory();
    }
    setShowHistory(!showHistory);
  };

  const handleUpdateOrCreateQualification = async () => {
    if (isEditMode && (!qualificationData || !qualificationData.id)) {
      toast.error("Missing qualification data for update");
      return;
    }
    // Determine which user ID to use
    const targetUserId = selectedUser?.id || userId;
    
    if (!isEditMode && (!targetUserId || !qualificationDetails.qualificationTypeId)) {
      toast.error("Missing user ID or qualification type for creation");
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem("access_token");

      let formattedExpiryDate = null;
      if (qualificationDetails.expiryDate) {
        const date = new Date(qualificationDetails.expiryDate);
        formattedExpiryDate = date.toISOString().split('T')[0];
      }

      const formData = new FormData();
      formData.append("certificate_number", qualificationDetails.certificateNumber);
      formData.append("expiry_date", formattedExpiryDate || "");
      if (qualificationDetails.certificate_file) {
        formData.append("certificate_file", qualificationDetails.certificate_file);
      }
      if (!isEditMode) {
        formData.append("user", targetUserId);
        formData.append("qualification_type", qualificationDetails.qualificationTypeId);
      }

      const url = isEditMode
        ? `${API_BASE_URL}/userqualification/${qualificationData.id}/`
        : `${API_BASE_URL}/userqualification/`;
      const method = isEditMode ? axios.patch : axios.post;

      const response = await method(url, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      if (hasMultipleFiles) {
        // Mark current file as processed
        setProcessedFiles(prev => {
          const updated = [...prev];
          updated[currentFileIndex] = true;
          return updated;
        });

        // Check if there are more files to process
        const nextUnprocessedIndex = processedFiles.findIndex((processed, index) => 
          !processed && index > currentFileIndex
        );

        if (nextUnprocessedIndex !== -1) {
          // Move to next file
          setCurrentFileIndex(nextUnprocessedIndex);
          // Reset form for next file
          setQualificationDetails({
            certificateNumber: "",
            expiryDate: "",
            certificate_file: droppedFiles[nextUnprocessedIndex],
            qualificationTypeId: null,
          });
          toast.success(`File ${currentFileIndex + 1} processed successfully. Continue with file ${nextUnprocessedIndex + 1}.`);
          return; // Don't close dialog, continue with next file
        } else {
          // All files processed
          toast.success(`All ${droppedFiles.length} qualification files have been processed successfully!`);
        }
      } else {
        toast.success(isEditMode ? "Qualification updated successfully" : "Qualification created successfully");
      }

      onClose();
      fetchData();
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} qualification:`, error.response?.data || error);
      toast.error(`Failed: ${JSON.stringify(error.response?.data)}`);
    } finally {
      setLoading(false);
    }
  };


  const handleSkipFile = () => {
    // Mark current file as skipped
    setSkippedFiles(prev => new Set([...prev, currentFileIndex]));
    
    // Mark current file as processed (skipped)
    setProcessedFiles(prev => {
      const updated = [...prev];
      updated[currentFileIndex] = true;
      return updated;
    });

    // Find next unprocessed file
    const nextUnprocessedIndex = processedFiles.findIndex((processed, index) => 
      !processed && index > currentFileIndex
    );

    if (nextUnprocessedIndex !== -1) {
      setCurrentFileIndex(nextUnprocessedIndex);
      setQualificationDetails({
        certificateNumber: "",
        expiryDate: "",
        certificate_file: droppedFiles[nextUnprocessedIndex],
        qualificationTypeId: null,
      });
      toast.info(`File ${currentFileIndex + 1} skipped. Continue with file ${nextUnprocessedIndex + 1}.`);
    } else {
      // All files processed or skipped
      const processedCount = processedFiles.filter(Boolean).length;
      toast.success(`File processing completed. ${processedCount} files processed.`);
      onClose();
      fetchData();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      if (!open) {
        onClose();
      }
    }}>
      <DialogContent className="sm:max-w-[600px] max-h-[95vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <FileText className="h-5 w-5 mr-2 text-blue-600" />
            <span>
              {isEditMode ? "Update Qualification" : "Create New Qualification"}
              {hasMultipleFiles && ` (${currentFileIndex + 1} of ${droppedFiles.length})`}
            </span>
          </DialogTitle>
        </DialogHeader>

        {/* Multi-file progress */}
        {hasMultipleFiles && (
          <div className="bg-blue-50 p-3 rounded-lg border border-blue-200 mb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <span className="text-sm font-medium text-blue-800">
                  File {currentFileIndex + 1} of {droppedFiles.length}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleSkipFile}
                  className="text-orange-600 border-orange-300 hover:bg-orange-50"
                >
                  Skip This File
                </Button>
              </div>
            </div>
            {currentFile && (
              <div className="mt-2 text-xs text-blue-700">
                Current file: <span className="font-medium">{currentFile.name}</span>
                {currentFile.size && ` (${Math.round(currentFile.size / 1024)} KB)`}
              </div>
            )}
            <div className="mt-2 text-xs text-gray-600">
              Progress: {processedFiles.filter(Boolean).length - skippedFiles.size} saved, {skippedFiles.size} skipped, {processedFiles.filter(p => !p).length} remaining
            </div>
          </div>
        )}

        <div className="space-y-4 my-4">
          {/* File Parse Information */}
          {!isEditMode && fileParseInfo && (
            <div className="bg-green-50 p-3 rounded-lg border border-green-200">
              <h3 className="text-sm font-medium text-green-800 mb-2">Auto-detected from filename</h3>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <span className="text-green-700 font-medium">Training Number:</span>
                  <div className="text-green-800">{fileParseInfo.trainingNumber}</div>
                </div>
                <div>
                  <span className="text-green-700 font-medium">Username:</span>
                  <div className="text-green-800">{fileParseInfo.username}</div>
                </div>
                <div>
                  <span className="text-green-700 font-medium">Training Date:</span>
                  <div className="text-green-800">{fileParseInfo.trainingDate.toLocaleDateString()}</div>
                </div>
                {fileParseInfo.qualificationNumber && (
                  <div>
                    <span className="text-green-700 font-medium">Qualification Number:</span>
                    <div className="text-green-800">{fileParseInfo.qualificationNumber}</div>
                  </div>
                )}
                {selectedUser && (
                  <div>
                    <span className="text-green-700 font-medium">User Found:</span>
                    <div className="text-green-800">{selectedUser.first_name} {selectedUser.last_name}</div>
                  </div>
                )}
              </div>
              {qualificationDetails.qualificationTypeId && (
                <div className="mt-2 p-2 bg-green-100 rounded text-xs">
                  <span className="text-green-700 font-medium">Qualification Type Matched:</span>
                  <div className="text-green-800">
                    {qualificationTypes.find(q => q.value === qualificationDetails.qualificationTypeId)?.label || `ID: ${qualificationDetails.qualificationTypeId}`}
                  </div>
                  <div className="text-green-700 mt-1">
                    <span className="font-medium">Calculated Expiry:</span> {qualificationDetails.expiryDate ? new Date(qualificationDetails.expiryDate + 'T00:00:00').toLocaleDateString() : 'Not set'}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Parsing failed warning */}
          {!isEditMode && currentFile && !fileParseInfo && (
            <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
              <h3 className="text-sm font-medium text-yellow-800 mb-1">Manual Input Required</h3>
              <p className="text-xs text-yellow-700">
                Could not auto-detect information from filename "{currentFile.name}". 
                <br />Expected format: "username-trainingNumber-date-qualificationNumber"
                <br />Examples (spaces are ignored):
                <br />• "john.doe-16-15.3.25-ABC123"
                <br />• "john.doe - 16 - 15.3.25 - ABC123"
                <br />Note: qualification number is optional
              </p>
            </div>
          )}

          {/* Specific warnings for parsed data */}
          {!isEditMode && fileParseInfo && (parseWarnings.qualificationNotFound || parseWarnings.userNotFound || parseWarnings.dateInvalid) && (
            <div className="space-y-2">
              {parseWarnings.qualificationNotFound && (
                <div className="bg-orange-50 p-3 rounded-lg border border-orange-200">
                  <h3 className="text-sm font-medium text-orange-800 mb-1">Qualification Not Found</h3>
                  <p className="text-xs text-orange-700">
                    Nie odnaleziono pasującej kwalifikacji dla numeru treningu "{fileParseInfo.trainingNumber}". 
                    Popraw nazwę pliku lub wybierz ręcznie Qualification Type.
                  </p>
                </div>
              )}
              
              {parseWarnings.userNotFound && (
                <div className="bg-orange-50 p-3 rounded-lg border border-orange-200">
                  <h3 className="text-sm font-medium text-orange-800 mb-1">User Not Found</h3>
                  <p className="text-xs text-orange-700">
                    Nie znaleziono użytkownika o nazwie "{fileParseInfo.username}". 
                    Popraw nazwę pliku lub plik został upuszczony na niewłaściwego użytkownika.
                  </p>
                </div>
              )}
              
              {parseWarnings.dateInvalid && (
                <div className="bg-orange-50 p-3 rounded-lg border border-orange-200">
                  <h3 className="text-sm font-medium text-orange-800 mb-1">Invalid Date Format</h3>
                  <p className="text-xs text-orange-700">
                    Nieprawidłowy format daty. Oczekiwany format: dd.mm.yy lub dd.mm.yyyy
                    <br />Przykłady: 15.3.25, 15.03.2025
                  </p>
                </div>
              )}
            </div>
          )}

          {isEditMode ? (
            <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
              <h3 className="text-sm font-medium text-blue-800 mb-1">Qualification Details</h3>
              <p className="text-xs text-blue-700 font-medium">
                {qualificationData?.qualification_type_data?.name || "Unnamed qualification"}
              </p>
              {qualificationData?.expiry_date && (
                <div className={`text-xs mt-1 ${new Date(qualificationData.expiry_date) < new Date() ? "text-red-600 font-medium" : "text-gray-600"}`}>
                  Current expiry: {new Date(qualificationData.expiry_date).toLocaleDateString()}
                </div>
              )}
              {qualificationData?.certificate_file && (
                <div className="mt-2">
                  <a
                    href={qualificationData.certificate_file}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center p-2 bg-blue-100 rounded border border-blue-200 text-blue-700 text-xs"
                  >
                    <FileText className="h-3 w-3 mr-1" />
                    View Certificate File
                  </a>
                </div>
              )}

              {/* Version History Section */}
              <div className="mt-3 border-t border-blue-200 pt-3">
                <button
                  type="button"
                  onClick={toggleHistory}
                  className="w-full flex items-center justify-between px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100 rounded transition-colors"
                >
                  <span className="flex items-center gap-1">
                    <History className="w-3 h-3" />
                    Version History
                  </span>
                  {showHistory ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                </button>

                {showHistory && (
                  <div className="mt-2 text-xs text-gray-600">
                    {loadingHistory ? (
                      <span>Loading...</span>
                    ) : versionHistory.length === 0 ? (
                      <span>No previous versions</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {versionHistory.map((version) => (
                          <span
                            key={version.id}
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${
                              version.is_current ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                            }`}
                          >
                            v{version.version}
                            {version.certificate_number && <span>• #{version.certificate_number}</span>}
                            {version.expiry_date && <span>• {new Date(version.expiry_date).toLocaleDateString()}</span>}
                            {version.certificate_file && (
                              <a href={version.certificate_file} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 ml-1">
                                <Eye className="w-3 h-3" />
                              </a>
                            )}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div>
              <label className="block text-sm text-gray-700 mb-1">Qualification Type</label>
              <Select
                options={qualificationTypes}
                value={qualificationTypes.find(type => type.value === qualificationDetails.qualificationTypeId) || null}
                onChange={(selected) =>
                  setQualificationDetails((prev) => ({
                    ...prev,
                    qualificationTypeId: selected ? selected.value : null,
                  }))
                }
                placeholder="Select or search for a qualification type"
                isClearable
                isSearchable
                className="text-sm"
              />
            </div>
          )}
          <div>
            <label className="block text-sm text-gray-700 mb-1">Certificate Number</label>
            <input
              type="text"
              className="w-full p-2 text-sm border rounded"
              value={qualificationDetails.certificateNumber}
              onChange={(e) =>
                setQualificationDetails((prev) => ({ ...prev, certificateNumber: e.target.value }))
              }
              placeholder="Enter certificate number"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Expiry Date</label>
            <input
              type="date"
              className="w-full p-2 text-sm border rounded"
              value={qualificationDetails.expiryDate}
              onChange={(e) =>
                setQualificationDetails((prev) => ({ ...prev, expiryDate: e.target.value }))
              }
            />
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">
              Upload Certificate File
              {currentFile && (
                <span className="text-blue-600 font-medium ml-2">
                  (File already attached: {currentFile.name})
                </span>
              )}
            </label>
            {!currentFile ? (
              <input
                type="file"
                className="w-full p-2 text-sm border rounded"
                onChange={(e) =>
                  setQualificationDetails((prev) => ({
                    ...prev,
                    certificate_file: e.target.files[0] || null,
                  }))
                }
              />
            ) : (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center">
                  <FileText className="h-4 w-4 text-green-600 mr-2" />
                  <span className="text-sm text-green-800 font-medium">{currentFile.name}</span>
                  <span className="text-xs text-green-600 ml-2">
                    ({Math.round(currentFile.size / 1024)} KB)
                  </span>
                </div>
                <p className="text-xs text-green-700 mt-1">
                  File is ready to be uploaded with this qualification.
                </p>
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdateOrCreateQualification}
            disabled={loading || (!isEditMode && !qualificationDetails.qualificationTypeId)}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {loading 
              ? (isEditMode ? "Updating..." : "Creating...") 
              : (isEditMode 
                  ? "Update Qualification" 
                  : hasMultipleFiles 
                    ? `Save & Continue (${currentFileIndex + 1}/${droppedFiles.length})`
                    : "Create Qualification"
              )
            }
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditQualificationDialog;